/* ============================================================
   AM2T — Training Pack Builder
   <training-pack-builder> web component.
   - Each top option has its OWN size select (data-for="tee|longsleeve").
   - Only the selected top's size select is visible.
   - Socks has dynamic size selector (3 sizes required to enable CTA).
   - Atomic /cart/add.js with items[] for all 3 products.
   - The displayed total is the fixed pack price (data-pack-price on the
     element, set from the section's "Pack price" setting). If it is not
     present, it falls back to the sum of the products.
   ============================================================ */

(() => {
  'use strict';

  if (customElements.get('training-pack-builder')) return;

  class TrainingPackBuilder extends HTMLElement {
    connectedCallback() {
      this.selectedTop = 'tee';
      this.selectedSizes = { top: null, bottom: null, socks: null };
      this.products = {};

      this.topRadios = this.querySelectorAll('input[name="pack-top"]');
      this.topSizeSelects = this.querySelectorAll('[data-top-size]'); // one per top
      this.bottomSizeSelect = this.querySelector('[data-bottom-size]');
      this.socksSizeSelect = this.querySelector('[data-socks-size]');
      this.totalEl = this.querySelector('[data-total]');
      this.addBtn = this.querySelector('[data-add-pack]');
      this.errorEl = this.querySelector('[data-error]');

      if (!this.addBtn) return;

      this.bindEvents();
      this.loadAllProducts().then(() => {
        this.applyTopSelection(this.selectedTop);
        this.updateTotal();
        this.updateButtonState();
      });
    }

    bindEvents() {
      this.topRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          if (radio.checked) this.handleTopChange(radio.value);
        });
      });

      this.topSizeSelects.forEach((sel) => {
        sel.addEventListener('change', (e) => {
          // Only the visible (active) select drives selectedSizes.top.
          if (sel.dataset.for !== this.selectedTop) return;
          this.selectedSizes.top = e.target.value ? parseInt(e.target.value, 10) : null;
          this.updateButtonState();
        });
      });

      this.bottomSizeSelect?.addEventListener('change', (e) => {
        this.selectedSizes.bottom = e.target.value ? parseInt(e.target.value, 10) : null;
        this.updateButtonState();
      });

      this.socksSizeSelect?.addEventListener('change', (e) => {
        this.selectedSizes.socks = e.target.value ? parseInt(e.target.value, 10) : null;
        this.updateButtonState();
      });

      this.addBtn.addEventListener('click', () => this.addPackToCart());
    }

    async loadAllProducts() {
      const handles = {
        tee: this.dataset.teeHandle,
        longsleeve: this.dataset.longsleeveHandle,
        shorts: this.dataset.shortsHandle,
        socks: this.dataset.socksHandle
      };

      const fetches = Object.entries(handles).map(async ([key, handle]) => {
        if (!handle) return [key, null];
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return [key, await res.json()];
        } catch (err) {
          console.warn(`[training-pack] failed to fetch ${handle}:`, err);
          return [key, null];
        }
      });

      const results = await Promise.all(fetches);
      results.forEach(([key, data]) => { this.products[key] = data; });

      if (!this.products.shorts || !this.products.socks) {
        this.showError('One or more pack products are currently unavailable.');
        this.addBtn.disabled = true;
        return;
      }

      // Populate per-top size selects, plus shorts + socks.
      this.topSizeSelects.forEach((sel) => {
        const productKey = sel.dataset.for;
        const product = this.products[productKey];
        if (product) this.populateSizes(sel, product.variants);
      });
      this.populateSizes(this.bottomSizeSelect, this.products.shorts.variants);
      this.populateSizes(this.socksSizeSelect, this.products.socks.variants);
    }

    handleTopChange(value) {
      this.selectedTop = value;
      this.applyTopSelection(value);
      // Reset top size; the new top's select starts at "Size" placeholder.
      this.selectedSizes.top = null;
      this.updateTotal();
      this.updateButtonState();
    }

    applyTopSelection(value) {
      // Show only the active top's size select; hide the other.
      this.topSizeSelects.forEach((sel) => {
        const isActive = sel.dataset.for === value;
        sel.hidden = !isActive;
        if (!isActive) sel.value = '';
      });
    }

    populateSizes(selectEl, variants) {
      if (!selectEl) return;
      selectEl.innerHTML = '<option value="">Size</option>';
      variants.forEach((v) => {
        if (!v.available) return;
        const opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.title === 'Default Title' ? 'One size' : v.title;
        selectEl.appendChild(opt);
      });
      if (selectEl.options.length === 1) {
        const opt = document.createElement('option');
        opt.value = '';
        opt.textContent = 'Sold out';
        opt.disabled = true;
        selectEl.appendChild(opt);
      }
    }

    updateTotal() {
      if (!this.totalEl) return;

      // Fixed pack price (from the section setting) takes priority.
      const packPrice = parseFloat(this.dataset.packPrice);
      if (!Number.isNaN(packPrice) && packPrice > 0) {
        this.totalEl.textContent = Math.round(packPrice);
        return;
      }

      // Fallback: sum of the individual products.
      const top = this.products[this.selectedTop];
      if (!top) return;
      const topPrice = top.price || 0;
      const shortsPrice = this.products.shorts?.price || 0;
      const socksPrice = this.products.socks?.price || 0;
      const totalCents = topPrice + shortsPrice + socksPrice;
      this.totalEl.textContent = Math.round(totalCents / 100);
    }

    updateButtonState() {
      const ready = this.selectedSizes.top && this.selectedSizes.bottom && this.selectedSizes.socks;
      this.addBtn.disabled = !ready;
      this.addBtn.classList.toggle('is-ready', !!ready);
    }

    showError(msg) {
      if (this.errorEl) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }
    }

    clearError() {
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }

    async addPackToCart() {
      if (!this.selectedSizes.top || !this.selectedSizes.bottom || !this.selectedSizes.socks) return;

      this.clearError();
      this.addBtn.disabled = true;
      const labelEl = this.addBtn.querySelector('[data-add-label]');
      const originalLabel = labelEl?.textContent ?? this.addBtn.textContent;
      this.setBtnLabel('ADDING…');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              { id: this.selectedSizes.top, quantity: 1 },
              { id: this.selectedSizes.bottom, quantity: 1 },
              { id: this.selectedSizes.socks, quantity: 1 }
            ]
          })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { cart, source: 'training-pack' }
        }));

        this.setBtnLabel('ADDED ✓');
        setTimeout(() => {
          this.setBtnLabel(originalLabel);
          this.addBtn.disabled = false;
        }, 1500);
      } catch (err) {
        console.error('[training-pack] add failed:', err);
        this.showError('Could not add pack. Please try again.');
        this.setBtnLabel('TRY AGAIN');
        setTimeout(() => {
          this.setBtnLabel(originalLabel);
          this.addBtn.disabled = false;
        }, 2000);
      }
    }

    setBtnLabel(text) {
      const labelEl = this.addBtn.querySelector('[data-add-label]');
      if (labelEl) labelEl.textContent = text;
      else this.addBtn.textContent = text;
    }
  }

  customElements.define('training-pack-builder', TrainingPackBuilder);
})();